import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { SupplierPaymentSummary } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import { usePayableSuppliersQuery, useSupplierPaymentsQuery } from '../api/payables.api';
import { SupplierPaymentStatusBadge } from './PayableBadges';

/**
 * What one supplier is owed, shown on the supplier's own page.
 *
 * A window into payables rather than a second payables screen: the figures and the last few
 * payments, and a link to the real thing. It needs `payables.view` and nothing else — reading what
 * a supplier is owed is a finance question, not a reason to be able to edit the supplier record.
 */
export function SupplierPayablesCard({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { format } = useCurrency();

  const positions = usePayableSuppliersQuery({ supplierId });
  const payments = useSupplierPaymentsQuery({ supplierId, pageSize: 5 });

  const position = positions.data?.find((row) => row.supplierId === supplierId);

  const columns: Column<SupplierPaymentSummary>[] = [
    {
      key: 'reference',
      header: t('payables.fields.paymentReference'),
      primary: true,
      render: (payment) => (
        <span className="text-ink font-mono text-sm" dir="ltr">
          {payment.reference}
        </span>
      ),
    },
    {
      key: 'paymentDate',
      header: t('payables.fields.paymentDate'),
      render: (payment) => (
        <span className="text-muted text-sm" dir="ltr">
          {payment.paymentDate}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('payables.fields.amount'),
      align: 'end',
      render: (payment) => (
        <span className="text-ink" dir="ltr">
          {format(payment.amountMinor)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('payables.fields.paymentStatus'),
      render: (payment) => <SupplierPaymentStatusBadge status={payment.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (payment) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openPaymentNamed', { reference: payment.reference })}
          onClick={() => void navigate(`/finance/payables/payments/${payment.id}`)}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t('payables.supplierCardTitle')}
        subtitle={t('payables.supplierCardHint')}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void navigate(`/finance/payables/invoices?supplierId=${supplierId}`)}
          >
            {t('payables.openPayables')}
          </Button>
        }
        wrapAction
      />
      <CardBody className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-0.5">
          <p className="text-muted text-xs">{t('payables.fields.outstanding')}</p>
          <p className="text-ink text-lg font-semibold" dir="ltr">
            {format(position?.outstandingMinor ?? 0)}
          </p>
        </div>
        <div className="space-y-0.5">
          <p className="text-muted text-xs">{t('payables.fields.overdue')}</p>
          <p
            className={
              (position?.overdueMinor ?? 0) > 0
                ? 'text-lg font-semibold text-red-700'
                : 'text-ink text-lg font-semibold'
            }
            dir="ltr"
          >
            {format(position?.overdueMinor ?? 0)}
          </p>
        </div>
        <div className="space-y-0.5">
          <p className="text-muted text-xs">{t('payables.fields.oldestDue')}</p>
          <p className="text-ink text-lg font-semibold" dir="ltr">
            {position?.oldestDueDate ?? '—'}
          </p>
        </div>
      </CardBody>
      <CardHeader title={t('payables.recentPayments')} />
      <DataTable
        caption={t('payables.recentPayments')}
        columns={columns}
        rows={payments.data?.items}
        rowKey={(payment) => payment.id}
        isLoading={payments.isPending}
        emptyTitle={t('payables.payments.empty')}
      />
    </Card>
  );
}
